import Accordion from "@/components/Accordion";
import { LanguageConfigModel, StyleConfigModel } from "@/pages/Messaging/types";
import { Toggle } from "@velaro/velaro-component-lib";
import React from "react";
import TextInput from "../../TextInput";
import { ChatWindow } from "../../types";

interface Props {
  onExpanded(): void;
  workingChatWindow: ChatWindow;
  setWorkingChatWindow: (chatWindow: ChatWindow) => void;
}

const QUEUE_FORMULAS = [
  {
    value: "realtime",
    label: "Live average",
    description: "Calculated from the current queue depth and recent handle times. Most accurate when agents are active."
  },
  {
    value: "historical",
    label: "Historical average",
    description: "Based on the last 7 days of handle time data. Stable and predictable, but may lag during unusual volume."
  },
  {
    value: "position",
    label: "Position × handle time",
    description: "Queue position multiplied by average handle time. Transparent and easy to explain to visitors."
  },
  {
    value: "minimum",
    label: "Minimum guarantee",
    description: "Always show at least N minutes even if the calculated time is shorter. Prevents unrealistic promises."
  },
  {
    value: "fixed",
    label: "Fixed message",
    description: "Show a custom message instead of a calculated time, such as 'typically 5–10 minutes'."
  },
];

const SOUND_OPTIONS = [
  { value: "none", label: "None" },
  { value: "chime", label: "Chime" },
  { value: "doubleChime", label: "Double Chime" },
  { value: "bell", label: "Bell" },
  { value: "ping", label: "Ping" },
];

export function Conversation(props: Props) {
  const workingLanguageConfig = props.workingChatWindow.languageConfig || {};
  const workingStyleConfig = props.workingChatWindow.styleConfig || {};

  const updateLanguageConfig = (updates: Partial<LanguageConfigModel>) => {
    props.setWorkingChatWindow({
      ...props.workingChatWindow,
      languageConfig: { ...workingLanguageConfig, ...updates }
    });
  };

  const updateStyleConfig = (updates: Partial<StyleConfigModel>) => {
    props.setWorkingChatWindow({
      ...props.workingChatWindow,
      styleConfig: { ...workingStyleConfig, ...updates }
    });
  };

  const queueFormula = workingStyleConfig.queueWaitTimeFormula ?? "realtime";
  const showEmailTranscript = workingStyleConfig.enableEmailTranscriptButton ?? false;
  const showQueueWaitTime = workingStyleConfig.enableQueueWaitTime ?? false;

  return (
    <Accordion.Item
      eventKey="conversation"
      onExpanded={() => props.onExpanded()}
    >
      <Accordion.Header>
        <Accordion.Title>Conversation</Accordion.Title>
        <Accordion.SubTitle>
          Text strings and behavior for the chat experience
        </Accordion.SubTitle>
      </Accordion.Header>
      <Accordion.Body>
        <div className="mb-2 space-y-4">
          {/* Text strings */}
          <div className="text-sm font-medium mb-2">Default Intro Text</div>
          <TextInput
            value={workingLanguageConfig.defaultGroupIntroText ?? ""}
            setValue={(value) =>
              updateLanguageConfig({ defaultGroupIntroText: value })
            }
            showEmojiButton={false}
          />
          <div className="text-sm font-medium my-2">Message Placeholder</div>
          <TextInput
            value={workingLanguageConfig.placeholderText ?? ""}
            setValue={(value) =>
              updateLanguageConfig({ placeholderText: value })
            }
            showEmojiButton={false}
          />
          <div className="text-sm font-medium my-2">Queue Header Text</div>
          <TextInput
            value={workingLanguageConfig.queueHeaderText ?? ""}
            setValue={(value) =>
              updateLanguageConfig({ queueHeaderText: value })
            }
            showEmojiButton={false}
          />
          <div className="text-sm font-medium my-2">Queue Position Label</div>
          <TextInput
            value={workingLanguageConfig.queuePositionText ?? ""}
            setValue={(value) =>
              updateLanguageConfig({ queuePositionText: value })
            }
            showEmojiButton={false}
          />

          <div className="my-4 border-t border-slate-200" />

          {/* New message sound */}
          <div>
            <div className="text-sm font-medium mb-2">New Message Sound</div>
            <div className="flex flex-wrap gap-2">
              {SOUND_OPTIONS.map((opt) => {
                const isSelected = (workingStyleConfig.newMessageSound ?? "doubleChime") === opt.value;
                return (
                  <div
                    key={opt.value}
                    className={`rounded-lg py-1.5 px-4 cursor-pointer border-2 text-sm ${
                      isSelected
                        ? "border-primary bg-primary-bg text-primary-active"
                        : "border-slate-300 bg-white text-slate-600"
                    }`}
                    onClick={() => updateStyleConfig({ newMessageSound: opt.value })}
                  >
                    {opt.label}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="my-4 border-t border-slate-200" />

          {/* Queue wait time */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <div>
                <div className="text-sm font-normal">Show Estimated Wait Time</div>
                <div className="text-xs text-slate-400">Display a wait time estimate to visitors in queue</div>
              </div>
              <Toggle
                toggled={showQueueWaitTime}
                changeToggle={() =>
                  updateStyleConfig({ enableQueueWaitTime: !showQueueWaitTime })
                }
              />
            </div>

            {showQueueWaitTime && (
              <div className="pl-3 border-l-2 border-slate-200 space-y-3 mt-2">
                <div className="text-sm font-medium">Calculation Method</div>
                <div className="space-y-2">
                  {QUEUE_FORMULAS.map((f) => {
                    const isSelected = queueFormula === f.value;
                    return (
                      <div
                        key={f.value}
                        className={`rounded-lg p-3 cursor-pointer border-2 ${
                          isSelected
                            ? "border-primary bg-primary-bg"
                            : "border-slate-200 bg-white"
                        }`}
                        onClick={() => updateStyleConfig({ queueWaitTimeFormula: f.value })}
                      >
                        <div className={`text-sm font-medium ${isSelected ? "text-primary-active" : "text-slate-700"}`}>
                          {f.label}
                        </div>
                        <div className="text-xs text-slate-400 mt-0.5">{f.description}</div>
                      </div>
                    );
                  })}
                </div>

                {queueFormula === "minimum" && (
                  <div>
                    <div className="text-sm font-medium mb-1">Minimum wait time (minutes)</div>
                    <input
                      type="number"
                      className="border border-slate-300 rounded-md p-2 w-24 text-sm"
                      min={1}
                      max={60}
                      value={workingStyleConfig.queueMinWaitMinutes ?? 1}
                      onChange={(e) =>
                        updateStyleConfig({ queueMinWaitMinutes: Math.max(1, parseInt(e.target.value) || 1) })
                      }
                    />
                  </div>
                )}

                {queueFormula === "fixed" && (
                  <div>
                    <div className="text-sm font-medium mb-1">Wait time message</div>
                    <TextInput
                      value={workingLanguageConfig.queuePositionText ?? ""}
                      setValue={(value) => updateLanguageConfig({ queuePositionText: value })}
                      showEmojiButton={false}
                      ariaLabel="Fixed wait time message"
                    />
                    <div className="text-xs text-slate-400 mt-1">e.g. "typically 5–10 minutes"</div>
                  </div>
                )}

                {(queueFormula === "realtime" || queueFormula === "historical" || queueFormula === "position") && (
                  <div>
                    <div className="text-sm font-medium mb-1">Add buffer (minutes)</div>
                    <div className="text-xs text-slate-400 mb-2">Added to the calculated time to set conservative expectations</div>
                    <input
                      type="number"
                      className="border border-slate-300 rounded-md p-2 w-24 text-sm"
                      min={0}
                      max={30}
                      value={workingStyleConfig.queueWaitTimeBuffer ?? 0}
                      onChange={(e) =>
                        updateStyleConfig({ queueWaitTimeBuffer: Math.max(0, parseInt(e.target.value) || 0) })
                      }
                    />
                  </div>
                )}

                <div>
                  <div className="text-sm font-medium mb-2">Queue Timer</div>
                  <div className="space-y-1">
                    {[
                      { value: "none", label: "No timer", description: "Show only the position or estimate, no live counter" },
                      { value: "countdown", label: "Countdown", description: "Counts down from the estimated wait — reassuring, shows progress" },
                      { value: "elapsed", label: "Elapsed", description: "Counts up from when the visitor joined queue" },
                    ].map((opt) => {
                      const isSelected = (workingStyleConfig.queueTimerMode ?? "none") === opt.value;
                      return (
                        <div
                          key={opt.value}
                          className={`rounded-lg p-3 cursor-pointer border-2 ${
                            isSelected
                              ? "border-primary bg-primary-bg"
                              : "border-slate-200 bg-white"
                          }`}
                          onClick={() => updateStyleConfig({ queueTimerMode: opt.value })}
                        >
                          <div className={`text-sm font-medium ${isSelected ? "text-primary-active" : "text-slate-700"}`}>
                            {opt.label}
                          </div>
                          <div className="text-xs text-slate-400 mt-0.5">{opt.description}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-between items-center">
              <div className="text-sm font-normal">Enable Print Button</div>
              <Toggle
                toggled={workingStyleConfig.enablePrintButton ?? false}
                changeToggle={() =>
                  updateStyleConfig({ enablePrintButton: !workingStyleConfig.enablePrintButton })
                }
              />
            </div>

            <div>
              <div className="flex justify-between items-center">
                <div className="text-sm font-normal">Enable Email Transcript</div>
                <Toggle
                  toggled={showEmailTranscript}
                  changeToggle={() =>
                    updateStyleConfig({ enableEmailTranscriptButton: !showEmailTranscript })
                  }
                />
              </div>
              {showEmailTranscript && (
                <div className="pl-3 border-l-2 border-slate-200 mt-3 space-y-3">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Email Modal Text</div>
                  {[
                    { label: "Modal Title", field: "emailModalTitle" as keyof LanguageConfigModel, placeholder: "Send Email" },
                    { label: "Input Label", field: "emailModalInputLabel" as keyof LanguageConfigModel, placeholder: "Email Address" },
                    { label: "Validation Message", field: "emailModalValidationText" as keyof LanguageConfigModel, placeholder: "Please enter a valid email address" },
                    { label: "Cancel Button", field: "emailModalCancelText" as keyof LanguageConfigModel, placeholder: "Cancel" },
                    { label: "Submit Button", field: "emailModalSubmitText" as keyof LanguageConfigModel, placeholder: "Submit" },
                  ].map(({ label, field }) => (
                    <div key={field}>
                      <div className="text-sm font-medium mb-1">{label}</div>
                      <TextInput
                        value={(workingLanguageConfig[field] as string) ?? ""}
                        setValue={(value) => updateLanguageConfig({ [field]: value })}
                        showEmojiButton={false}
                        ariaLabel={label}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-between items-center">
              <div className="text-sm font-normal">Enable File Upload</div>
              <Toggle
                toggled={workingStyleConfig.enableUploadButton ?? false}
                changeToggle={() =>
                  updateStyleConfig({ enableUploadButton: !workingStyleConfig.enableUploadButton })
                }
              />
            </div>
            <div className="flex justify-between items-center">
              <div className="text-sm font-normal">Enable Chat Rating</div>
              <Toggle
                toggled={workingStyleConfig.enableChatRatingStars ?? false}
                changeToggle={() =>
                  updateStyleConfig({ enableChatRatingStars: !workingStyleConfig.enableChatRatingStars })
                }
              />
            </div>
            <div className="flex justify-between items-center">
              <div className="text-sm font-normal">Enable Message Ratings</div>
              <Toggle
                toggled={workingStyleConfig.enableChatLineRatings ?? false}
                changeToggle={() =>
                  updateStyleConfig({ enableChatLineRatings: !workingStyleConfig.enableChatLineRatings })
                }
              />
            </div>

            <div className="flex justify-between items-center">
              <div>
                <div className="text-sm font-normal">Use 24-hour timestamps</div>
                <div className="text-xs text-slate-400">Show times as 14:30 instead of 2:30 PM</div>
              </div>
              <Toggle
                toggled={workingStyleConfig.use24HourTimestamps ?? false}
                changeToggle={() =>
                  updateStyleConfig({ use24HourTimestamps: !workingStyleConfig.use24HourTimestamps })
                }
              />
            </div>
          </div>

          <div className="my-4 border-t border-slate-200" />

          {/* Timestamp display mode */}
          <div>
            <div className="text-sm font-medium mb-2">Timestamp Visibility</div>
            <div className="flex flex-wrap gap-2">
              {[
                { value: "always", label: "Always visible" },
                { value: "grouped", label: "Grouped (first in run)" },
                { value: "hover", label: "On hover" },
              ].map((opt) => {
                const isSelected = (workingStyleConfig.timestampDisplay ?? "always") === opt.value;
                return (
                  <div
                    key={opt.value}
                    className={`rounded-lg py-1.5 px-4 cursor-pointer border-2 text-sm ${
                      isSelected
                        ? "border-primary bg-primary-bg text-primary-active"
                        : "border-slate-300 bg-white text-slate-600"
                    }`}
                    onClick={() => updateStyleConfig({ timestampDisplay: opt.value })}
                  >
                    {opt.label}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Typing animation */}
          <div>
            <div className="text-sm font-medium mb-2">Typing Indicator Style</div>
            <div className="flex flex-wrap gap-2">
              {[
                { value: "dots", label: "Dots" },
                { value: "pulse", label: "Pulse" },
                { value: "wave", label: "Wave" },
              ].map((opt) => {
                const isSelected = (workingStyleConfig.typingAnimationStyle ?? "dots") === opt.value;
                return (
                  <div
                    key={opt.value}
                    className={`rounded-lg py-1.5 px-4 cursor-pointer border-2 text-sm ${
                      isSelected
                        ? "border-primary bg-primary-bg text-primary-active"
                        : "border-slate-300 bg-white text-slate-600"
                    }`}
                    onClick={() => updateStyleConfig({ typingAnimationStyle: opt.value })}
                  >
                    {opt.label}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Read receipts */}
          <div>
            <div className="text-sm font-medium mb-2">Read Receipts</div>
            <div className="flex flex-wrap gap-2">
              {[
                { value: "ticks", label: "✓✓ Double tick" },
                { value: "singleTick", label: "✓ Single tick" },
                { value: "seen", label: "\"Seen\" text" },
                { value: "none", label: "None" },
              ].map((opt) => {
                const isSelected = (workingStyleConfig.readReceiptStyle ?? "ticks") === opt.value;
                return (
                  <div
                    key={opt.value}
                    className={`rounded-lg py-1.5 px-4 cursor-pointer border-2 text-sm ${
                      isSelected
                        ? "border-primary bg-primary-bg text-primary-active"
                        : "border-slate-300 bg-white text-slate-600"
                    }`}
                    onClick={() => updateStyleConfig({ readReceiptStyle: opt.value })}
                  >
                    {opt.label}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="my-4 border-t border-slate-200" />

          {/* Message behavior toggles */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <div>
                <div className="text-sm font-normal">Group consecutive messages</div>
                <div className="text-xs text-slate-400">Merge runs of messages from the same sender into one bubble group</div>
              </div>
              <Toggle
                toggled={workingStyleConfig.groupConsecutiveMessages ?? true}
                changeToggle={() => updateStyleConfig({ groupConsecutiveMessages: !(workingStyleConfig.groupConsecutiveMessages ?? true) })}
              />
            </div>
            <div className="flex justify-between items-center">
              <div>
                <div className="text-sm font-normal">Render Markdown</div>
                <div className="text-xs text-slate-400">Bold, italic, links, and lists in agent messages</div>
              </div>
              <Toggle
                toggled={workingStyleConfig.enableMarkdown ?? true}
                changeToggle={() => updateStyleConfig({ enableMarkdown: !(workingStyleConfig.enableMarkdown ?? true) })}
              />
            </div>
            <div className="flex justify-between items-center">
              <div>
                <div className="text-sm font-normal">Link previews</div>
                <div className="text-xs text-slate-400">Show a preview card when a URL is shared in chat</div>
              </div>
              <Toggle
                toggled={workingStyleConfig.enableLinkPreview ?? true}
                changeToggle={() => updateStyleConfig({ enableLinkPreview: !(workingStyleConfig.enableLinkPreview ?? true) })}
              />
            </div>
            <div className="flex justify-between items-center">
              <div>
                <div className="text-sm font-normal">Emoji reactions</div>
                <div className="text-xs text-slate-400">Allow visitors and agents to react to individual messages</div>
              </div>
              <Toggle
                toggled={workingStyleConfig.enableEmojiReactions ?? false}
                changeToggle={() => updateStyleConfig({ enableEmojiReactions: !workingStyleConfig.enableEmojiReactions })}
              />
            </div>
          </div>
        </div>
      </Accordion.Body>
    </Accordion.Item>
  );
}
